import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { MsalService } from '@azure/msal-angular';

import { AccountInfo } from '@azure/msal-browser';
import { firstValueFrom, from, switchMap } from 'rxjs';
import { PictureSize } from '../enums/pictureSize.enum';
import { IRole } from '../../features/home/interfaces/role.interface';
import { Constants } from '../enums/constants.enum';
import { IWidgetUserTitle } from '../../features/home/interfaces/widget.user-title.interface';
import { APP_CONFIG } from '../../app.tokens';

@Injectable({
  providedIn: 'root',
})
export class CurrentUserService {
  private currentUserSignal = signal<AccountInfo | null>(null);
  readonly currentUser = this.currentUserSignal.asReadonly();
  private currentRoleSignal = signal<IRole | null>(null);
  readonly currentRole = this.currentRoleSignal.asReadonly();
  config = inject(APP_CONFIG);
  private msalService = inject(MsalService);
  private http = inject(HttpClient);

  private graphUrl = 'https://graph.microsoft.com/v1.0/me/photos';

  /**
   * The `getMsalData` function in TypeScript handles MSAL redirect observable to set the active
   * account or select the first account if none is active.
   */
  getMsalData(): void {
    this.msalService.handleRedirectObservable().subscribe(async (result) => {
      if (result && result.account) {
        this.msalService.instance.setActiveAccount(result.account);
        this.currentUserSignal.set(result.account);
        return;
      } else {
        const activeAccount = this.msalService.instance.getActiveAccount();
        if (activeAccount) {
          this.currentUserSignal.set(activeAccount);
        } else {
          const accounts = this.msalService.instance.getAllAccounts();
          if (accounts.length > 0) {
            this.msalService.instance.setActiveAccount(accounts[0]);
            this.currentUserSignal.set(accounts[0]);
          }
          return;
        }
      }
    });
  }

  /**
   * The function `getUserPhoto` asynchronously retrieves a user's photo in a specified size using
   * Microsoft Graph API and MSAL authentication.
   * @param {PictureSize} pictureSize - The `pictureSize` parameter in the `getUserPhoto` function is
   * used to specify the size of the user's photo that you want to retrieve. It is of type
   * `PictureSize`, which likely represents the dimensions of the photo (e.g., 100x100, 200x200,
   * @returns A Promise that resolves to a Blob containing the user's photo in the specified picture
   * size.
   */
  async getUserPhoto(pictureSize: PictureSize): Promise<Blob> {
    const picture$ = from(
      this.msalService.instance.acquireTokenSilent({
        scopes: ['User.Read'],
      }),
    ).pipe(
      switchMap((response) => {
        const headers = {
          Authorization: `Bearer ${response.accessToken}`,
        };
        return this.http.get(
          `${this.graphUrl}/${pictureSize}x${pictureSize}/$value`,
          { headers, responseType: 'blob' },
        );
      }),
    );
    return await firstValueFrom(picture$);
  }
  /**
   *
   *update the role using the signal approach
   * @param {IRole} role
   * @memberof CurrentUserService
   */
  changeRole(role: IRole) {
    this.currentRoleSignal.set(role);
  }

  /**
   * Retrieves the current user's title widget data from the backend service.
   *
   * Executes a single HTTP GET request to the user title widget endpoint (assembled
   * from the configured API base URL and widget-related constants) and resolves with
   * the typed response body. The method converts the underlying observable to a
   * promise via `firstValueFrom`, awaiting only the first (and only) emission.
   *
   * Behavior:
   * - On success: Resolves with the `IWidgetUserTitle` payload extracted from the HTTP response body.
   * - On failure: Rejects with an `HttpErrorResponse` (e.g., network error, non-2xx status).
   *
   * Notes:
   * - No client-side caching, retries, or fallback logic are applied.
   * - No side effects; the method strictly performs a fetch and returns data.
   * - Response metadata (headers, status) are not exposed to the caller.
   *
   * Example:
   * ```ts
   * const widget = await currentUserService.getUserTitleWidgetData();
   * console.log(widget?.positionName);
   * ```
   *
   * @returns Promise<IWidgetUserTitle> Resolves with the current user's title widget data.
   * @throws HttpErrorResponse If the HTTP request fails or returns an error status code.
   */
  async getUserTitleWidgetData(): Promise<IWidgetUserTitle> {
    const widgetData$ = this.http.get<IWidgetUserTitle>(
      `${this.config.apiUrl}/${Constants.WIDGETS}/${Constants.USER_TITLE}`,
      {
        observe: 'response',
      },
    );
    const widgetData = await firstValueFrom(widgetData$);
    return widgetData.body as IWidgetUserTitle;
  }
}
